/**
 =========================================================
 * Soft UI Dashboard React - v4.0.1
 =========================================================

 * Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
 * Copyright 2023 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// @mui material components
import Grid from "@mui/material/Grid"

// Soft UI Dashboard React components
import SoftBox from "soft_dashboard/components/SoftBox"

// Soft UI Dashboard React examples
import DashboardLayout from "soft_dashboard/examples/LayoutContainers/DashboardLayout"
import DashboardNavbar from "soft_dashboard/examples/Navbars/DashboardNavbar"
import ReportsBarChart from "soft_dashboard/examples/Charts/BarCharts/ReportsBarChart"

// Soft UI Dashboard React base styles
import typography from "soft_dashboard/assets/theme/base/typography"

// Dashboard layout components
import OrdersOverview from "./OrdersOverview/OrdersOverview"

// Data
import reportsBarChartData from "soft_dashboard/layouts/dashboard/data/reportsBarChartData"
import React, { useEffect, useState } from "react"
import Axios from "../../Util/Axios"
import Toast, { TOAST_SEVERITY_ERROR } from "../core/Toast/Toast"
import MiniStatsCard from "./MiniStatsCard"
import GradientLineChart from "../core/GradientLineChart/GradientLineChart"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { updateToastData } from "../../slices/toastSlice"
import { getCurrencyStringFromNumber } from "../../Util/Util"
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

const Dashboard = () => {
  const { size } = typography
  const { chart, items } = reportsBarChartData
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const [statsData, setStatsData] = useState(null)
  const [graphTypeValue, setGraphTypeValue] = useState("financials")
  const navigate = useNavigate()
  const dispatch = useDispatch()
  useEffect(() => {
    Axios.get("/api/stats/", navigate)
      .then(({ data }) => {
        setStatsData(data)
      })
      .catch((e) => {
        dispatch(
          updateToastData({
            message: e,
            severity: TOAST_SEVERITY_ERROR,
          })
        )
      })
  }, [])

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={6} sm={6} xl={3}>
              <MiniStatsCard
                title={"Total Revenue"}
                count={getCurrencyStringFromNumber(statsData?.total_revenue)}
                percentage={null}
                icon={{ color: "info", component: "paid" }}
              />
            </Grid>
            <Grid item xs={6} sm={6} xl={3}>
              <MiniStatsCard
                title={"Monthly Revenue"}
                count={getCurrencyStringFromNumber(statsData?.monthly_revenue)}
                percentage={statsData?.monthly_revenue_percentage_change}
                icon={{ color: "info", component: "paid" }}
              />
            </Grid>
            <Grid item xs={6} sm={6} xl={3}>
              <MiniStatsCard
                title={"Total Orders"}
                count={statsData?.total_orders}
                icon={{ color: "info", component: "inventory2" }}
              />
            </Grid>
            <Grid item xs={6} sm={6} xl={3}>
              <MiniStatsCard
                title={"Monthly Orders"}
                count={statsData?.monthly_orders}
                percentage={statsData?.monthly_order_percentage_change}
                icon={{ color: "info", component: "inventory2" }}
              />
            </Grid>
          </Grid>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <FormControl>
                <Typography variant="h3" sx={{ marginBottom: 1, fontSize: { xs: "1.4rem", sm: "1.75rem", md: "2.2rem" } }}>
                  Sales Overview
                </Typography>
                <RadioGroup
                  sx={{ marginBottom: 1, marginLeft: 2 }}
                  row
                  aria-labelledby="demo-radio-buttons-group-label"
                  value={graphTypeValue}
                  name="radio-buttons-group"
                  onChange={(event, value) => {
                    setGraphTypeValue(value)
                  }}
                >
                  <FormControlLabel
                    value="financials"
                    control={<Radio />}
                    label="Financials"
                  />
                  <FormControlLabel
                    value="counts"
                    control={<Radio />}
                    label="Counts"
                  />
                </RadioGroup>
              </FormControl>

              <GradientLineChart
                title={`Sales Overview`}
                height="20.25rem"
                graphTypeValue={graphTypeValue}
                chart={
                  graphTypeValue === "financials"
                    ? statsData?.financial_line_chart_data
                    : statsData?.counts_line_chart_data
                }
              />
            </Grid>
            <Grid item xs={12} lg={4}>
              <OrdersOverview statsData={statsData} />
            </Grid>
          </Grid>
        </SoftBox>
        {/* Pipeline Breakdown */}
        {statsData?.pipeline && (
          <SoftBox mb={3}>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Order Pipeline
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(statsData.pipeline).map(([key, val]) => (
                <Grid item xs={6} sm={4} md={2.4} key={key}>
                  <SoftBox
                    sx={{
                      backgroundColor: "#152737",
                      borderRadius: 2,
                      p: 2,
                      textAlign: "center",
                      border: "1px solid rgba(200,185,154,0.1)",
                    }}
                  >
                    <Typography variant="h3" sx={{ color: "#c8b99a" }}>
                      {val.count}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#a0b0c0", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                      {val.label}
                    </Typography>
                  </SoftBox>
                </Grid>
              ))}
            </Grid>
          </SoftBox>
        )}

        {/* Year-over-Year & Recent Orders */}
        <Grid container spacing={3}>
          {/* YoY Comparison */}
          {statsData?.yoy && (
            <Grid item xs={12} md={4}>
              <SoftBox sx={{ backgroundColor: "#152737", borderRadius: 2, p: 3, border: "1px solid rgba(200,185,154,0.1)", height: "100%" }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Year over Year
                </Typography>
                <SoftBox sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[
                    { label: "Revenue", thisYear: getCurrencyStringFromNumber(statsData.yoy.this_year_revenue), lastYear: getCurrencyStringFromNumber(statsData.yoy.last_year_revenue) },
                    { label: "Orders", thisYear: statsData.yoy.this_year_orders, lastYear: statsData.yoy.last_year_orders },
                    { label: "Cost", thisYear: getCurrencyStringFromNumber(statsData.yoy.this_year_cost), lastYear: getCurrencyStringFromNumber(statsData.yoy.last_year_cost) },
                  ].map(({ label, thisYear, lastYear }) => (
                    <SoftBox key={label} sx={{ borderBottom: "1px solid rgba(200,185,154,0.1)", pb: 1.5 }}>
                      <Typography variant="caption" sx={{ color: "#8a9bb0", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: "0.65rem" }}>
                        {label}
                      </Typography>
                      <SoftBox sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                        <SoftBox>
                          <Typography variant="caption" sx={{ color: "#5a6a7a", fontSize: "0.6rem" }}>This Year</Typography>
                          <Typography variant="h6" sx={{ color: "#f5f2ec" }}>{thisYear}</Typography>
                        </SoftBox>
                        <SoftBox sx={{ textAlign: "right" }}>
                          <Typography variant="caption" sx={{ color: "#5a6a7a", fontSize: "0.6rem" }}>Last Year</Typography>
                          <Typography variant="h6" sx={{ color: "#8a9bb0" }}>{lastYear}</Typography>
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  ))}
                </SoftBox>
              </SoftBox>
            </Grid>
          )}

          {/* Recent Orders */}
          {statsData?.recent_orders && (
            <Grid item xs={12} md={8}>
              <SoftBox sx={{ backgroundColor: "#152737", borderRadius: 2, p: 3, border: "1px solid rgba(200,185,154,0.1)" }}>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Recent Orders
                </Typography>
                <SoftBox component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {(isMobile ? ["Customer", "Status", "Amount"] : ["Customer", "Type", "Status", "Amount", "Date"]).map((h) => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 12px", borderBottom: "1px solid rgba(200,185,154,0.15)", color: "#8a9bb0", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap" }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {statsData.recent_orders.map((order, i) => (
                      <tr
                        key={i}
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/staff/orders/${order.customer_facing_id}`)}
                      >
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(200,185,154,0.07)", color: "#f5f2ec", fontSize: "0.85rem" }}>
                          {order.customer_name || "—"}
                        </td>
                        {!isMobile && (
                          <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(200,185,154,0.07)", color: "#a0b0c0", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                            {order.type}
                          </td>
                        )}
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(200,185,154,0.07)", color: "#c8b99a", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                          {order.status}
                        </td>
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(200,185,154,0.07)", color: "#f5f2ec", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                          {getCurrencyStringFromNumber(order.total_paid)}
                        </td>
                        {!isMobile && (
                          <td style={{ padding: "10px 12px", borderBottom: "1px solid rgba(200,185,154,0.07)", color: "#8a9bb0", fontSize: "0.8rem", whiteSpace: "nowrap" }}>
                            {order.created_at}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </SoftBox>
              </SoftBox>
            </Grid>
          )}
        </Grid>
      </SoftBox>
    </DashboardLayout>
  )
}

export default Dashboard
